import React, { useState, useRef, useCallback } from "react";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { UploadIcon, FileIcon, CrossIcon, TrashIcon } from "../../Icons";
import {
  ContentContainer,
  ModalTitle,
  ModalDescription,
  DropZone,
  DropZoneText,
  DropZoneLink,
  FileListContainer,
  FileItem,
  FileInfo,
  FileName,
  ProgressBar,
  RemoveButton,
  ButtonsContainer,
  CancelButton,
  ImportButton,
  ResultImage,
  ResultTitle,
  ResultDescription,
  DoneButton,
  ErrorList,
  ErrorItem,
  HiddenInput,
} from "./ImportFeesModal.styled";
import { colors } from "../../../theme/colors";
import importSuccessImage from "../../../assets/images/import-success.jpg";
import importFailedImage from "../../../assets/images/import-failed.jpg";
import { ImportFeesResult } from "../../../services/organizationService";

type ModalState = "upload" | "importing" | "success" | "failed";

interface FileWithProgress {
  file: File;
  progress: number;
  error?: boolean;
}

interface ImportFeesModalContentProps {
  onImport: (file: File) => Promise<ImportFeesResult>;
  onClose: () => void;
}

const ImportFeesModalContent: React.FC<ImportFeesModalContentProps> = ({
  onImport,
  onClose,
}) => {
  const { t } = useTranslation();
  const [modalState, setModalState] = useState<ModalState>("upload");
  const [files, setFiles] = useState<FileWithProgress[]>([]);
  const [isDragging, setIsDragging] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Prevent default drag behavior on the entire container
  const preventDefaultDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const validateFile = useCallback(
    (file: File): string | null => {
      const validTypes = [
        "text/csv",
        "application/vnd.ms-excel",
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      ];
      const validExtensions = [".csv", ".xlsx", ".xls"];

      const hasValidExtension = validExtensions.some((ext) =>
        file.name.toLowerCase().endsWith(ext),
      );

      if (!validTypes.includes(file.type) && !hasValidExtension) {
        return t(
          "importFeesModal.errors.invalidFormat",
          "Invalid file format. Please upload a .csv or .xlsx file.",
        );
      }

      return null;
    },
    [t],
  );

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const droppedFiles = Array.from(e.dataTransfer.files);
      const validFiles: FileWithProgress[] = [];

      droppedFiles.forEach((file) => {
        const error = validateFile(file);
        if (!error) {
          validFiles.push({ file, progress: 0 });
        }
      });

      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
      }
    },
    [validateFile],
  );

  const handleFileSelect = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const selectedFiles = Array.from(e.target.files || []);
      const validFiles: FileWithProgress[] = [];

      selectedFiles.forEach((file) => {
        const error = validateFile(file);
        if (!error) {
          validFiles.push({ file, progress: 0 });
        }
      });

      if (validFiles.length > 0) {
        setFiles((prev) => [...prev, ...validFiles]);
      }

      // Reset input
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    },
    [validateFile],
  );

  const handleRemoveFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const [successCount, setSuccessCount] = useState(0);

  const handleImport = useCallback(async () => {
    if (files.length === 0) return;

    setModalState("importing");
    setErrors([]);

    try {
      // Simulate progress while uploading
      for (let i = 0; i < files.length; i++) {
        for (let progress = 0; progress <= 80; progress += 20) {
          await new Promise((resolve) => setTimeout(resolve, 100));
          setFiles((prev) =>
            prev.map((f, idx) => (idx === i ? { ...f, progress } : f)),
          );
        }
      }

      // Call the actual import function with the first file
      const result = await onImport(files[0].file);

      // Complete progress
      setFiles((prev) => prev.map((f) => ({ ...f, progress: 100 })));

      // Check result
      if (result.failedCount > 0 && result.errors.length > 0) {
        // Show specific errors from API
        const errorMessages = result.errors.map(
          (err) => `Row ${err.rowNumber}: ${err.cdtCode} - ${err.errorMessage}`,
        );
        setErrors(errorMessages);

        if (result.successCount > 0) {
          // Partial success - some rows imported, some failed
          setSuccessCount(result.successCount);
          setModalState("failed");
        } else {
          // Complete failure
          setModalState("failed");
        }
      } else if (result.successCount > 0) {
        // All successful
        setSuccessCount(result.successCount);
        setModalState("success");
      } else {
        // No rows processed
        setErrors([
          t(
            "importFeesModal.errors.noValidData",
            "No valid data found in the file.",
          ),
        ]);
        setModalState("failed");
      }
    } catch (error: unknown) {
      console.error("Import error:", error);

      // Handle different error types
      const axiosError = error as {
        response?: { status?: number; data?: { detail?: string } };
      };

      if (axiosError.response?.status === 400) {
        setErrors([
          axiosError.response.data?.detail ||
            t(
              "importFeesModal.errors.invalidData",
              "The file contains invalid or missing data. Please fix the errors and try again.",
            ),
        ]);
      } else {
        setErrors([
          t(
            "importFeesModal.errors.systemError",
            "An error occurred while importing fees. Please try again.",
          ),
        ]);
      }
      setModalState("failed");
    }
  }, [files, onImport, t]);

  const handleClickUpload = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  // Success state
  if (modalState === "success") {
    return (
      <ContentContainer>
        <ResultImage src={importSuccessImage} alt="Import successful" />
        <ResultTitle>
          {t("importFeesModal.success.title", "Import Successful")}
        </ResultTitle>
        <ResultDescription>
          {t(
            "importFeesModal.success.description",
            "Import finished successfully. {{count}} fees have been updated.",
            { count: successCount },
          )}
        </ResultDescription>
        <DoneButton variant="contained" onClick={onClose}>
          {t("importFeesModal.success.done", "Done")}
        </DoneButton>
      </ContentContainer>
    );
  }

  // Failed state
  if (modalState === "failed") {
    return (
      <ContentContainer>
        <ResultImage src={importFailedImage} alt="Import failed" />
        <ResultTitle>
          {t("importFeesModal.failed.title", "Import Failed")}
        </ResultTitle>
        {successCount > 0 && (
          <ResultDescription>
            {t(
              "importFeesModal.failed.partialSuccess",
              "{{count}} fees were successfully updated, but some rows failed:",
              { count: successCount },
            )}
          </ResultDescription>
        )}
        <ErrorList>
          {errors.map((error, index) => (
            <ErrorItem key={index}>{error}</ErrorItem>
          ))}
        </ErrorList>
        <DoneButton variant="contained" onClick={onClose}>
          {t("importFeesModal.failed.ok", "Ok")}
        </DoneButton>
      </ContentContainer>
    );
  }

  // Upload / Importing state
  return (
    <ContentContainer
      onDragEnter={preventDefaultDrag}
      onDragOver={preventDefaultDrag}
      onDrop={preventDefaultDrag}
    >
      <ModalTitle>{t("importFeesModal.title", "Import Fees")}</ModalTitle>
      <ModalDescription>
        {t(
          "importFeesModal.description",
          "Please upload a .csv or .xlsx file with 3 columns: CDT Code, Description, and Fee.",
        )}
      </ModalDescription>

      <DropZone
        isDragging={isDragging}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClickUpload}
      >
        <UploadIcon width="24" height="24" fill={colors.primary.main} />
        <DropZoneText>
          {t("importFeesModal.dropzone.text", "Drag and drop your file here")}
          <br />
          {t("importFeesModal.dropzone.or", "or")}{" "}
          <DropZoneLink>
            {t("importFeesModal.dropzone.click", "Click to upload")}
          </DropZoneLink>
        </DropZoneText>
      </DropZone>

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleFileSelect}
      />

      {files.length > 0 && (
        <FileListContainer>
          {files.map((fileData, index) => (
            <FileItem key={index}>
              <FileIcon width="20" height="20" fill={colors.text.secondary} />
              <FileInfo>
                <FileName>{fileData.file.name}</FileName>
                <ProgressBar
                  progress={fileData.progress}
                  error={fileData.error}
                />
              </FileInfo>
              {modalState === "upload" ? (
                <RemoveButton onClick={() => handleRemoveFile(index)}>
                  <CrossIcon
                    width="16"
                    height="16"
                    fill={colors.text.secondary}
                  />
                </RemoveButton>
              ) : fileData.error ? (
                <TrashIcon width="20" height="20" fill={colors.status.error} />
              ) : null}
            </FileItem>
          ))}
        </FileListContainer>
      )}

      <ButtonsContainer>
        <CancelButton
          variant="outlined"
          onClick={onClose}
          disabled={modalState === "importing"}
        >
          {t("importFeesModal.cancel", "Cancel")}
        </CancelButton>
        <ImportButton
          variant="contained"
          onClick={handleImport}
          disabled={files.length === 0 || modalState === "importing"}
        >
          {modalState === "importing" ? (
            <CircularProgress size={20} color="inherit" />
          ) : (
            t("importFeesModal.import", "Import")
          )}
        </ImportButton>
      </ButtonsContainer>
    </ContentContainer>
  );
};

export default ImportFeesModalContent;
